import React, { memo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useUser } from 'Context/User';
import Uppy from '@uppy/core';
import Transloadit from '@uppy/transloadit';

import { AddMoistureLogReadingModal } from 'Components/RocketDry';

import { useRocketDryFunctions } from 'Context/RocketDry';

import { numberInputRegex } from 'Containers/RocketDry/helpers';

import { createDryingLog } from 'Containers/RocketDry/actions';
import { areEqual } from 'Utils/equalityChecks';

function AddMoistureLogReadingModalContainer() {
  const dispatch = useDispatch();

  const {
    selectedRoomId,
    isAddMoistureLogReadingModalOpen,
    setIsAddMoistureLogReadingModalOpen,
    selectedDamageMaterial,
    setMoistureLogRefetch,
  }: any = useRocketDryFunctions();

  const { id: userId } = useUser();

  const [latestAverage, setLatestAverage] = useState('');
  const [photo, setPhoto] = useState<File>();
  const [removed, setRemoved] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const onChangeLatestAverage = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setLatestAverage(filtered);
  }, []);

  const onRemovedCheckboxClick = useCallback(() => {
    setRemoved((value) => !value);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsAddMoistureLogReadingModalOpen(false);
    setLatestAverage('');
    setPhoto(null);
    setRemoved(false);
    setIsProcessing(false);
  }, []);

  const onAddReadingButtonClick = useCallback(async () => {
    if (selectedRoomId && selectedDamageMaterial) {
      setIsProcessing(true);
      const params: any = {
        removed,
      };
      if (!removed) {
        params.reading = latestAverage;
      }
      const response: any = await dispatch(createDryingLog(selectedRoomId, selectedDamageMaterial.materialId, params));
      if (photo && response?.data?.id) {
        const uppy = new Uppy().use(Transloadit, {
          assemblyOptions: {
            params: {
              auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
              template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
              fields: {
                user_id: userId,
                source: 'webapp',
                moisture_log_id: response.data.id,
              },
            },
          },
          waitForEncoding: true,
        });

        uppy.on('transloadit:complete', () => {
          // wait a bit before refetching, database needs to be synced first
          setTimeout(() => {
            setMoistureLogRefetch(true);
            onCloseModal();
          }, 1000);
        });

        uppy.addFile({
          name: photo.name,
          type: photo.type,
          data: photo,
        });

        uppy.upload();
      } else {
        setMoistureLogRefetch(true);
        onCloseModal();
      }
    }
  }, [selectedRoomId, selectedDamageMaterial, latestAverage, userId, photo, removed]);

  return (
    <AddMoistureLogReadingModal
      isOpen={isAddMoistureLogReadingModalOpen}
      closeModal={onCloseModal}
      formErrors={{}}
      isProcessing={isProcessing}
      latestAverage={latestAverage}
      photo={photo}
      removed={removed}
      onChangeLatestAverage={onChangeLatestAverage}
      onRemovedCheckboxClick={onRemovedCheckboxClick}
      setPhoto={setPhoto}
      onAddReadingButtonClick={onAddReadingButtonClick}
    />
  );
}

const AddMoistureLogReadingModalMemo = memo(AddMoistureLogReadingModalContainer, areEqual);

export { AddMoistureLogReadingModalMemo as AddMoistureLogReadingModal };
